import { gql, useQuery } from "@apollo/client";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

const STREAM_TEAM_MEMBER_ANALYTICS_QUERY = gql`
    query StreamTeamMemberAnalytics($memberId: UUID!) {
        streamTeamMemberAnalytics(memberId: $memberId) {
            streamTeamMember {
                id
                isAcademy
                player {
                    name
                }
                twitchUsername
            }
            trackedViewerCount
            totalRavenguardPoints
            totalRunestoneRedemptions
            scheduledStreamCount
            scheduledStreamsLast7Days
            totalLiveSessions
            liveSessionsLast7Days
            liveDaysLast30Days
            liveSessions {
                id
                startedAt
                endedAt
                streamTitle
                gameName
                peakViewerCount
                lastViewerCount
            }
            viewerAnalytics {
                player {
                    id
                    name
                    clan {
                        tag
                    }
                }
                ravenguardPoints
                runestoneRedemptionCount
            }
        }
    }
`;

const TABLE_PAGE_SIZE = 15;

export default function StreamTeamMemberAnalyticsPage() {
    const { memberId } = useParams<{ memberId: string }>();
    const [tablePage, setTablePage] = useState(1);

    const { data, loading, error } = useQuery(
        STREAM_TEAM_MEMBER_ANALYTICS_QUERY,
        {
            variables: { memberId },
            skip: !memberId,
        },
    );

    const analytics = data?.streamTeamMemberAnalytics;
    const viewerAnalytics = analytics?.viewerAnalytics || [];
    const liveSessions = analytics?.liveSessions || [];
    const isAcademyMember = Boolean(analytics?.streamTeamMember?.isAcademy);

    const viewerTrendChartData = useMemo(() => {
        return [...liveSessions]
            .map((session: any, index: number) => ({
                id: `${session.startedAt}-${index}`,
                startedAtMs: new Date(session.startedAt).getTime(),
                startedAtLabel: new Date(session.startedAt).toLocaleString([], {
                    month: "short",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                }),
                peakViewerCount: session.peakViewerCount || 0,
                lastViewerCount: session.lastViewerCount || 0,
            }))
            .sort((a, b) => a.startedAtMs - b.startedAtMs);
    }, [liveSessions]);

    const recentLiveSessions = useMemo(() => {
        return [...liveSessions]
            .sort(
                (a: any, b: any) =>
                    new Date(b.startedAt).getTime() -
                    new Date(a.startedAt).getTime(),
            )
            .slice(0, 15);
    }, [liveSessions]);

    const totalPages = Math.max(
        1,
        Math.ceil(viewerAnalytics.length / TABLE_PAGE_SIZE),
    );

    useEffect(() => {
        setTablePage((current) => Math.min(current, totalPages));
    }, [totalPages]);

    const paginatedViewerAnalytics = useMemo(() => {
        const start = (tablePage - 1) * TABLE_PAGE_SIZE;
        const end = start + TABLE_PAGE_SIZE;
        return viewerAnalytics.slice(start, end);
    }, [viewerAnalytics, tablePage]);

    if (loading) {
        return (
            <div className="mx-auto mt-4 w-full px-4">
                <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <p className="text-slate-600 dark:text-slate-400">
                        Loading streamer analytics...
                    </p>
                </div>
            </div>
        );
    }

    if (error || !data?.streamTeamMemberAnalytics) {
        return (
            <div className="mx-auto mt-4 w-full px-4">
                <div className="rounded-2xl border border-red-300 bg-red-50 p-6 shadow-lg dark:border-red-700 dark:bg-red-900/20">
                    <p className="text-red-800 dark:text-red-400">
                        Failed to load streamer analytics.
                    </p>
                    <Link
                        to="/longhouse/stream-team"
                        className="mt-4 inline-block text-sm font-medium text-blue-700 underline dark:text-blue-300"
                    >
                        Back to Stream Team
                    </Link>
                </div>
            </div>
        );
    }

    if (!analytics) {
        return null;
    }

    return (
        <div className="mx-auto mt-4 w-full px-4">
            <div className="mb-6 rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                            {analytics.streamTeamMember.player.name} Analytics
                        </h1>
                        <p className="mt-2 text-slate-600 dark:text-slate-400">
                            Twitch:{" "}
                            {analytics.streamTeamMember.twitchUsername ||
                                "Not set"}
                        </p>
                    </div>
                    <Link
                        to="/longhouse/stream-team"
                        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                    >
                        Back to Stream Team
                    </Link>
                </div>
            </div>

            <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                <StatCard
                    label="Tracked Viewers"
                    value={analytics.trackedViewerCount}
                />
                {!isAcademyMember && (
                    <StatCard
                        label="Total RP"
                        value={analytics.totalRavenguardPoints}
                    />
                )}
                {!isAcademyMember && (
                    <StatCard
                        label="Total Runestone Redemptions"
                        value={analytics.totalRunestoneRedemptions}
                    />
                )}
                <StatCard
                    label="Scheduled Streams (All Time)"
                    value={analytics.scheduledStreamCount}
                />
                <StatCard
                    label="Scheduled Streams (Last 7 Days)"
                    value={analytics.scheduledStreamsLast7Days}
                />
                <StatCard
                    label="Live Sessions (All Time)"
                    value={analytics.totalLiveSessions}
                />
                <StatCard
                    label="Live Sessions (Last 7 Days)"
                    value={analytics.liveSessionsLast7Days}
                />
                <StatCard
                    label="Live Days (Last 30 Days)"
                    value={analytics.liveDaysLast30Days}
                />
            </div>

            <div className="mb-6 rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                    Stream Viewer Trends
                </h2>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                    Peak and ending viewer counts across tracked live sessions.
                </p>
                {viewerTrendChartData.length === 0 ? (
                    <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
                        No live session viewer data tracked yet.
                    </p>
                ) : (
                    <div className="mt-4 h-80 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={viewerTrendChartData}>
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="#334155"
                                />
                                <XAxis
                                    dataKey="startedAtLabel"
                                    tick={{ fill: "#94a3b8", fontSize: 12 }}
                                    interval="preserveStartEnd"
                                    minTickGap={24}
                                />
                                <YAxis
                                    tick={{ fill: "#94a3b8", fontSize: 12 }}
                                />
                                <Tooltip />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="peakViewerCount"
                                    name="Peak Viewers"
                                    stroke="#f59e0b"
                                    strokeWidth={2}
                                    dot={false}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="lastViewerCount"
                                    name="Last Viewers"
                                    stroke="#38bdf8"
                                    strokeWidth={2}
                                    dot={false}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            <div className="mb-6 rounded-2xl border border-slate-300 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <div className="border-b border-slate-200 px-6 py-4 dark:border-slate-700">
                    <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                        Recent Live Sessions
                    </h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                    Started
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                    Ended
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                    Stream
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                    Peak
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                    Last
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                    Details
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                            {recentLiveSessions.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={6}
                                        className="px-6 py-8 text-center text-sm text-slate-500 dark:text-slate-400"
                                    >
                                        No live sessions tracked yet.
                                    </td>
                                </tr>
                            ) : (
                                recentLiveSessions.map((session: any) => (
                                    <tr
                                        key={`${session.startedAt}-${session.streamTitle || "stream"}`}
                                        className="transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
                                    >
                                        <td className="px-6 py-4 text-slate-900 dark:text-slate-100">
                                            {new Date(
                                                session.startedAt,
                                            ).toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 text-slate-700 dark:text-slate-300">
                                            {session.endedAt
                                                ? new Date(
                                                      session.endedAt,
                                                  ).toLocaleString()
                                                : "Live"}
                                        </td>
                                        <td className="px-6 py-4 text-slate-700 dark:text-slate-300">
                                            <div className="font-medium text-slate-900 dark:text-slate-100">
                                                {session.streamTitle ||
                                                    "Untitled Stream"}
                                            </div>
                                            <div className="text-xs text-slate-500 dark:text-slate-400">
                                                {session.gameName || "Unknown"}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right font-semibold text-slate-900 dark:text-slate-100">
                                            {session.peakViewerCount}
                                        </td>
                                        <td className="px-6 py-4 text-right text-slate-700 dark:text-slate-300">
                                            {session.lastViewerCount}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <Link
                                                to={`/longhouse/stream-team/${memberId}/sessions/${session.id}`}
                                                className="text-sm font-medium text-blue-700 underline dark:text-blue-300"
                                            >
                                                View
                                            </Link>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {!isAcademyMember && (
                <div className="rounded-2xl border border-slate-300 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <div className="border-b border-slate-200 px-6 py-4 dark:border-slate-700">
                    <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                        Viewer RP Leaderboard
                    </h2>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                        Ordered by RP, then by runestone redemptions.
                    </p>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                    Viewer
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                    Clan
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                    RP
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                    Runestone Redemptions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                            {viewerAnalytics.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={4}
                                        className="px-6 py-8 text-center text-sm text-slate-500 dark:text-slate-400"
                                    >
                                        No RP or runestone redemption data
                                        tracked yet.
                                    </td>
                                </tr>
                            ) : (
                                paginatedViewerAnalytics.map((entry: any) => (
                                    <tr
                                        key={entry.player.id}
                                        className="transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
                                    >
                                        <td className="px-6 py-4 font-medium text-slate-900 dark:text-slate-100">
                                            {entry.player.name}
                                        </td>
                                        <td className="px-6 py-4 text-slate-700 dark:text-slate-300">
                                            {entry.player.clan?.tag || "-"}
                                        </td>
                                        <td className="px-6 py-4 text-right font-semibold text-slate-900 dark:text-slate-100">
                                            {entry.ravenguardPoints}
                                        </td>
                                        <td className="px-6 py-4 text-right text-slate-700 dark:text-slate-300">
                                            {entry.runestoneRedemptionCount}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-300">
                    <span>
                        Page {tablePage} of {totalPages}
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() =>
                                setTablePage((current) =>
                                    Math.max(1, current - 1),
                                )
                            }
                            disabled={tablePage <= 1}
                            className="rounded-lg border border-slate-300 px-3 py-1.5 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:hover:bg-slate-800"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() =>
                                setTablePage((current) =>
                                    Math.min(totalPages, current + 1),
                                )
                            }
                            disabled={tablePage >= totalPages}
                            className="rounded-lg border border-slate-300 px-3 py-1.5 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:hover:bg-slate-800"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
                </div>
            )}
        </div>
    );
}

function StatCard({ label, value }: { label: string; value: number }) {
    return (
        <div className="rounded-xl border border-slate-300 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase dark:text-slate-400">
                {label}
            </p>
            <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-slate-100">
                {value}
            </p>
        </div>
    );
}
